import React, {useEffect, useRef, useState} from "react";
import DatePicker from "react-datepicker";
import NewsModal from "../components/NewsModal";
import api from "../utils/axiosInstance";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/NewsList.css";

const SORTABLE_COLUMNS = {
    title: "title",
    createdAt: "created_at",
    publishDate: "publish_date",
    priority: "priority",
};

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const NewsList = () => {
    const [newsList, setNewsList] = useState([]);
    const [users, setUsers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [sortConfig, setSortConfig] = useState({sortBy: "created_at", sortOrder: "desc"});
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [pagination, setPagination] = useState({page: 1, perPage: 20, totalItems: 0, totalPages: 1});

    const [filters, setFilters] = useState({
        title: "",
        createdById: "",
        editorById: "",
        createdFrom: null,
        createdTo: null,
        publishFrom: null,
        publishTo: null,
        published: "",
        priority: "",
    });
    const [appliedFilters, setAppliedFilters] = useState(filters);
    const debounceRef = useRef(null);

    const toCamelCase = (obj) => {
        if (Array.isArray(obj)) return obj.map(toCamelCase);
        if (obj && typeof obj === "object") {
            return Object.fromEntries(
                Object.entries(obj).map(([key, value]) => [
                    key.replace(/_([a-z])/g, (_, c) => c.toUpperCase()),
                    toCamelCase(value),
                ])
            );
        }
        return obj;
    };

    const renderSortArrow = (column) => {
        if (sortConfig.sortBy !== column) return "⇅";
        return sortConfig.sortOrder === "asc" ? "↑" : "↓";
    };

    const handleSort = (column) => {
        setSortConfig((prev) => {
            if (prev.sortBy === column) {
                return {sortBy: column, sortOrder: prev.sortOrder === "asc" ? "desc" : "asc"};
            }
            return {sortBy: column, sortOrder: "asc"};
        });
    };

    const handleDuplicate = (news) => {
        const duplicatedNews = {
            ...news,
            id: null,
            createdAt: null,
            updatedAt: null,
            createdById: null,
            editorById: null,
            published: false,
            priority: false,
            publishDate: null,
            status: "new",
        };
        setSelectedNews(duplicatedNews);
        setModalOpen(true);
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get("/users");
            setUsers(response.data);
        } catch (err) {
            console.error("Failed to fetch users", err);
        }
    };

    const buildParams = () => ({
        title: appliedFilters.title?.trim() || undefined,
        created_by_id: appliedFilters.createdById || undefined,
        created_at_from: appliedFilters.createdFrom ? toUnixSeconds(appliedFilters.createdFrom) : undefined,
        created_at_to: appliedFilters.createdTo ? toUnixSeconds(endOfDay(appliedFilters.createdTo)) : undefined,
        publish_date_from: appliedFilters.publishFrom ? toUnixSeconds(appliedFilters.publishFrom) : undefined,
        publish_date_to: appliedFilters.publishTo ? toUnixSeconds(endOfDay(appliedFilters.publishTo)) : undefined,
        published: appliedFilters.published || undefined,
        priority: appliedFilters.priority || undefined,
        page,
        per_page: perPage,
        sort_by: sortConfig.sortBy,
        sort_order: sortConfig.sortOrder,
    });

    const fetchNews = async () => {
        setLoading(true);
        try {
            const response = await api.get("/news", {params: buildParams()});
            const normalized = toCamelCase(response.data);
            setNewsList(normalized.items ?? []);
            setPagination(normalized.pagination ?? {page: 1, perPage, totalItems: 0, totalPages: 1});
        } catch (err) {
            console.error("Failed to fetch news", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Debounce filter changes, then reset to page 1 and apply them.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setAppliedFilters(filters);
            setPage(1);
        }, 400);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Fetch whenever pagination, sorting, or applied filters change.
    useEffect(() => {
        fetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, sortConfig, appliedFilters]);

    const handleEdit = (news) => {
        setSelectedNews(news);
        setModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedNews(null);
        setModalOpen(true);
    };

    const handleSave = async () => {
        await fetchNews();
        setModalOpen(false);
    };

    const canDelete = (n) => {
        const hasPublicationDates =
            Array.isArray(n?.publicationDates) && n.publicationDates.length > 0;

        return !n?.published && !hasPublicationDates;
    };

    const handleDelete = async (id) => {
        if (deletingId) return;
        const item = newsList.find((n) => n.id === id);
        if (!item) return;

        if (!canDelete(item)) return;
        if (!window.confirm("Delete this news?")) return;

        try {
            setDeletingId(id);
            await api.delete(`/news/${id}`);
            if (newsList.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                await fetchNews();
            }
        } catch (err) {
            console.error("Failed to delete news", err);
            alert("Failed to delete news");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="news-container">
            <h1>News</h1>
            <button className="add-news-button" onClick={handleAdd}>➕ Add News</button>

            {/* Filters */}
            <div className="filter-panel">
                <div className="filter-row">
                    <input
                        type="text"
                        placeholder="Filter by title"
                        value={filters.title}
                        onChange={(e) => setFilters({...filters, title: e.target.value})}
                    />

                    <label>Created by:</label>
                    <select
                        value={filters.createdById}
                        onChange={(e) => setFilters({...filters, createdById: e.target.value})}
                    >
                        <option value="">All</option>
                        {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.email}</option>
                        ))}
                    </select>

                    <label>Published:</label>
                    <select
                        value={filters.published}
                        onChange={(e) => setFilters({...filters, published: e.target.value})}
                    >
                        <option value="">All</option>
                        <option value="true">Published</option>
                        <option value="false">Unpublished</option>
                    </select>

                    <label>Priority:</label>
                    <select
                        value={filters.priority}
                        onChange={(e) => setFilters({...filters, priority: e.target.value})}
                    >
                        <option value="">All</option>
                        <option value="true">Priority</option>
                        <option value="false">Normal</option>
                    </select>

                    <label>Created:</label>
                    <DatePicker
                        selectsRange
                        startDate={filters.createdFrom}
                        endDate={filters.createdTo}
                        onChange={([createdFrom, createdTo]) =>
                            setFilters({...filters, createdFrom, createdTo})
                        }
                        isClearable
                        placeholderText="Select range"
                        dateFormat="dd.MM.yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />

                    <label>Publish:</label>
                    <DatePicker
                        selectsRange
                        startDate={filters.publishFrom}
                        endDate={filters.publishTo}
                        onChange={([publishFrom, publishTo]) =>
                            setFilters({...filters, publishFrom, publishTo})
                        }
                        isClearable
                        placeholderText="Select range"
                        dateFormat="dd.MM.yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="table-scroll-wrapper">
                <table className="news-table">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Image</th>
                        <th onClick={() => handleSort(SORTABLE_COLUMNS.title)}>Title {renderSortArrow(SORTABLE_COLUMNS.title)}</th>
                        <th onClick={() => handleSort(SORTABLE_COLUMNS.createdAt)}>Created {renderSortArrow(SORTABLE_COLUMNS.createdAt)}</th>
                        <th>Updated</th>
                        <th onClick={() => handleSort(SORTABLE_COLUMNS.publishDate)}>Publish Date {renderSortArrow(SORTABLE_COLUMNS.publishDate)}</th>
                        <th>Created By</th>
                        <th>Editor</th>
                        <th>Published</th>
                        <th onClick={() => handleSort(SORTABLE_COLUMNS.priority)}>Priority {renderSortArrow(SORTABLE_COLUMNS.priority)}</th>
                        <th>Actions</th>
                    </tr>
                    </thead>

                    <tbody>
                    {newsList.map((news) => (
                        <tr key={news.id}>
                            <td className="clickable-cell" onClick={() => handleEdit(news)}>{news.id}</td>
                            <td className="thumb-cell" onClick={() => handleEdit(news)}>
                                {news.image ? (
                                    <img
                                        className="news-thumb"
                                        src={news.image}
                                        alt={news.title ?? `news-${news.id}`}
                                        loading="lazy"
                                        width="120"
                                        height="68"
                                    />
                                ) : (
                                    <div className="news-thumb placeholder" aria-label="no image"/>
                                )}
                            </td>
                            <td className="clickable-cell" onClick={() => handleEdit(news)}>{news.title}</td>
                            <td>{formatDate(news.createdAt)}</td>
                            <td>{formatDate(news.updatedAt)}</td>
                            <td>{formatDate(news.publishDate)}</td>
                            <td>{getUserEmailById(users, news.createdById)}</td>
                            <td>{getUserEmailById(users, news.editorById)}</td>
                            <td>{news.published ? "✔" : "✖"}</td>
                            <td>{news.priority ? "⭐" : ""}</td>
                            <td className="device-actions">
                                <button className="action-btn edit-button" onClick={() => handleEdit(news)}>✏️ Edit</button>
                                <button
                                    className="action-btn delete-button"
                                    onClick={() => handleDelete(news.id)}
                                    disabled={!canDelete(news) || deletingId === news.id}
                                    aria-disabled={!canDelete(news)}
                                    title={
                                        !canDelete(news)
                                            ? "Delete is available only for UNPUBLISHED news without PublicationDates"
                                            : deletingId === news.id
                                                ? "Deleting…"
                                                : "Delete"
                                    }
                                >
                                    {deletingId === news.id ? "⏳…" : "🗑️ Delete"}
                                </button>
                                <button
                                    className="action-btn duplicate-button"
                                    onClick={() => handleDuplicate(news)}
                                >
                                    📄 Duplicate
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination-bar">
                <div className="pagination-info">
                    {pagination.totalItems > 0
                        ? `${pagination.totalItems} items · page ${pagination.page} of ${Math.max(pagination.totalPages, 1)}`
                        : "No results"}
                </div>

                <div className="pagination-controls">
                    <label>Per page:</label>
                    <select
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        {PER_PAGE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>

                    <button
                        disabled={loading || page <= 1}
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    >
                        ← Prev
                    </button>
                    <button
                        disabled={loading || page >= pagination.totalPages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Next →
                    </button>
                </div>
            </div>

            {modalOpen && (
                <NewsModal
                    news={selectedNews}
                    onClose={() => setModalOpen(false)}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
};

const toUnixSeconds = (date) => Math.floor(new Date(date).getTime() / 1000);

const formatDate = (val) => {
    if (!val) return "";
    const d = new Date(val);

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return d.toLocaleString(navigator.language || "en-GB", {
        timeZone: userTimeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
};
const getUserEmailById = (users, id) => {
    const user = users.find((u) => u.id === id);
    return user ? user.email : "-";
};

export default NewsList;
